import type {
  ConvertQuoteDto,
  ConvertQuoteSplitDto,
  ConvertedQuoteSplitResponseDto,
  CreateQuoteDto,
  CreateQuoteLineItemDto,
  QuoteLineItemResponseDto,
  QuoteResponseDto,
  QuoteRevisionSummaryDto,
  QuotesControllerListQuotesParams,
  UpdateQuoteDto,
  UpdateQuoteLineItemDto,
  UpdateQuoteStatusDto,
} from '@trellis/api'
import type { BatchUpdateQuoteLineItems, RequestResult } from '@trellis/shared'

import { env } from '@/env'
import { apiClient } from '@/lib/api-client'
import { request, requestPaged } from '@/lib/request'
import { getStoredTenantSlug } from '@/lib/tenant'

export type QuoteDetailResponse = QuoteResponseDto & { invoiceId: string | null }
export type ConvertedQuoteResponse = QuoteResponseDto & { invoiceId: string }

export const listQuotes = (params: QuotesControllerListQuotesParams = {}) =>
  requestPaged<QuoteResponseDto>(() => apiClient.api.quotesControllerListQuotes(params))

export const getQuote = (id: string) =>
  request<QuoteDetailResponse>(() => apiClient.api.quotesControllerGetQuote({ id }))

export const createQuote = (data: CreateQuoteDto) =>
  request<QuoteResponseDto>(() => apiClient.api.quotesControllerCreateQuote(data))

export const updateQuote = (id: string, data: UpdateQuoteDto) =>
  request<QuoteResponseDto>(() => apiClient.api.quotesControllerUpdateQuote({ id }, data))

export const deleteQuote = (id: string) =>
  request<void>(() => apiClient.api.quotesControllerDeleteQuote({ id }))

export const transitionQuoteStatus = (id: string, data: UpdateQuoteStatusDto) =>
  request<QuoteResponseDto>(() =>
    apiClient.api.quotesControllerUpdateStatus({ id }, data),
  )

export const supersedeQuote = (id: string) =>
  request<QuoteResponseDto>(() => apiClient.api.quotesControllerSupersede({ id }))

export const archiveQuote = (id: string) =>
  request<QuoteResponseDto>(() => apiClient.api.quotesControllerArchiveQuote({ id }))

export const unarchiveQuote = (id: string) =>
  request<QuoteResponseDto>(() => apiClient.api.quotesControllerUnarchiveQuote({ id }))

export const convertQuote = (id: string, data: ConvertQuoteDto) =>
  request<ConvertedQuoteResponse>(() =>
    apiClient.api.quotesControllerConvert({ id }, data),
  )

export const convertQuoteSplit = (id: string, data: ConvertQuoteSplitDto) =>
  request<ConvertedQuoteSplitResponseDto>(() =>
    apiClient.api.quotesControllerConvertSplit({ id }, data),
  )

export const getQuoteRevisions = (id: string) =>
  request<QuoteRevisionSummaryDto[]>(() =>
    apiClient.api.quotesControllerGetRevisions({ id }),
  )

export async function getQuotePreview(id: string): Promise<RequestResult<string>> {
  try {
    const tenantSlug = getStoredTenantSlug()
    const response = await fetch(`${env.VITE_API_BASE_URL}/api/quotes/${id}/preview`, {
      credentials: 'include',
      headers: tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : undefined,
    })
    if (!response.ok) {
      return {
        status: response.status,
        data: null,
        pagination: null,
        error: { code: 'PREVIEW_ERROR', message: 'Failed to load quote preview' },
      }
    }
    const html = await response.text()
    return { status: 200, data: html, pagination: null, error: null }
  } catch (caughtError) {
    return {
      status: 500,
      data: null,
      pagination: null,
      error: {
        code: 'PREVIEW_ERROR',
        message:
          caughtError instanceof Error
            ? caughtError.message
            : 'Failed to load quote preview',
      },
    }
  }
}

export const listQuoteLineItems = (quoteId: string) =>
  request<QuoteLineItemResponseDto[]>(() =>
    apiClient.api.quoteLineItemsControllerListQuoteLineItems({ quoteId }),
  )

export const addQuoteLineItem = (quoteId: string, data: CreateQuoteLineItemDto) =>
  request<QuoteLineItemResponseDto>(() =>
    apiClient.api.quoteLineItemsControllerAddQuoteLineItem({ quoteId }, data),
  )

export const updateQuoteLineItem = (
  quoteId: string,
  itemId: string,
  data: UpdateQuoteLineItemDto,
) =>
  request<QuoteLineItemResponseDto>(() =>
    apiClient.api.quoteLineItemsControllerUpdateQuoteLineItem({ quoteId, itemId }, data),
  )

export const deleteQuoteLineItem = (quoteId: string, itemId: string) =>
  request<void>(() =>
    apiClient.api.quoteLineItemsControllerDeleteQuoteLineItem({ quoteId, itemId }),
  )

export const syncQuoteLineItem = (quoteId: string, itemId: string) =>
  request<QuoteLineItemResponseDto>(() =>
    apiClient.api.quoteLineItemsControllerSyncQuoteLineItem({ quoteId, itemId }),
  )

export const batchUpdateQuoteLineItems = (
  quoteId: string,
  data: BatchUpdateQuoteLineItems,
) =>
  request<QuoteLineItemResponseDto[]>(() =>
    apiClient.api.quoteLineItemsControllerBatchUpdateLineItems({ quoteId }, data),
  )

export const assignTagsToQuote = (quoteId: string, tagIds: string[]) =>
  request<void>(() =>
    apiClient.api.quotesControllerAssignTags({ id: quoteId }, { tagIds }),
  )

export const removeTagFromQuote = (quoteId: string, tagId: string) =>
  request<void>(() => apiClient.api.quotesControllerRemoveTag({ id: quoteId, tagId }))
